import { useEffect, useRef, useState } from "react";
import { getCustomisationValue, getGroupData } from "../../EditablePreview";
import InlineProgressBar, { convertCustomisationDataToBarSettings } from "../progress-bar/inline-progress-bar";
import VisualisedNode from "../VisualisedNode";
import './LivePoll.scss';
import { COLOUR_NAME_TO_COLOUR_HEX } from "../../../../../../../helpers/Colours";
import { IconJsxer } from "../../../../../../../helpers/IconHelper";

const POLL_DUMMY_OPTIONS = [
    {hashtag: '#A', text: 'Option 1', perc: 60, points: 1},
    {hashtag: '#B', text: 'Option 2', perc: 2, points: 10},
    {hashtag: '#C', text: 'Option 3', perc: 30, points: 2},
    {hashtag: '#D', text: 'Option 4', perc: 50, points: 5},
    {hashtag: '#E', text: 'Option 5', perc: 10, points: 20},
    {hashtag: '#F', text: 'Option 6', perc: 40, points: 1},
    {hashtag: '#G', text: 'Option 7', perc: 5, points: 15},
    {hashtag: '#H', text: 'Option 8', perc: 8, points: 8},
    {hashtag: '#I', text: 'Option 9', perc: 20, points: 3},
    {hashtag: '#J', text: 'Option 10', perc: 30, points: 4},
];

const lastPoll = {
    index: -1,
};

const LivePoll = (props) => {
    // console.log('LivePoll props', props);
    const previewScale = props.previewScale || 1;
    const campaignData = props.campaignData;

    let livePollNode = props.node;
    // console.log('LivePoll node', livePollNode);
    if (livePollNode.path.split('.')[1] !== 'Live_Poll_Panel') {
        // console.warn('LivePoll component being used on a node that is not a Live Poll Panel - this may cause issues.');
        // Try to find the Live Poll Panel node in the sub nodes
        for (const subNode of props.node.subNodes) {
            if (subNode.path.split('.')[1] === 'Live_Poll_Panel') {
                livePollNode = subNode;
                break;
            }
        }
    }

    const customisationVars = livePollNode.customisationVars;
    // The top node here is the main container - we can let VisualisedNode handle all of this bit, 
    // we are just going to set the height style to fit-content (polls don't have a dimensions / height configured and are always dynamic)
    // We are then going to control the children of this node...

    const pullOutPollData = () => {
        const pollData = getGroupData(customisationVars, 'setup.pollData.polls', lastPoll.index);
        // console.log('LivePoll pullOutPollData', pollData);
        const processedPollData = {
            type: pollData?.type || 'opinion',
            question: pollData?.question || 'Question Text',
            answers: [],
            points: [],
        };
        for (let i = 1; i <= 10; i++) {
            const answerText = pollData?.[`answer${i}`];
            if (answerText) {
                processedPollData.answers.push(
                    answerText
                );
                processedPollData.points.push(pollData?.[`points${i}`] ?? 0);
            } else {
                break;
            }
        }
        if (processedPollData.answers.length === 0) {
            processedPollData.answers.push('Option 1');
        }
        if (processedPollData.answers.length === 1) {
            processedPollData.answers.push('Option 2');
        }
        lastPoll.pollData = processedPollData;
        lastPoll.rawPollData = pollData;
    }
    let pollIndex = lastPoll.index;
    if (typeof props.activeNode?.dataIndex !== 'undefined') {
        pollIndex = props.activeNode.dataIndex;   
    }
    if (pollIndex >= getGroupData(customisationVars, 'setup.pollData.polls', -1).length) {
        pollIndex = getGroupData(customisationVars, 'setup.pollData.polls', -1).length - 1;
    }
    if (pollIndex !== lastPoll.index) {
        lastPoll.index = pollIndex;
        // console.log('LivePoll Setting up new poll index', pollIndex);
    }
    if (pollIndex !== -1) {
        pullOutPollData();
    }
    const usingPollData = lastPoll.index !== -1;

    const percBarSettings = convertCustomisationDataToBarSettings(getGroupData(livePollNode.subNodes[3].subNodes[3].customisationVars, 'hud.polls.percentageBarStyle'));
    percBarSettings.shapeAndPosition.shape[0].shape = 'square';
    const percBarWidth = 
       (getCustomisationValue(livePollNode.subNodes[3].subNodes[3].customisationVars, 'hud.polls.shapeSettings.panelWidth', 0, 'livePoll') 
        - getCustomisationValue(livePollNode.subNodes[3].subNodes[3].customisationVars, 'hud.polls.answerBoxStyle.hashtagAreaWidth', 0, 'livePoll') 
        - getCustomisationValue(livePollNode.subNodes[3].subNodes[3].customisationVars, 'hud.polls.answerBoxStyle.hashtagAreaSpacing', 0, 'livePoll')
        - 60)
        * previewScale;
    const hashtagAreaGap = getCustomisationValue(livePollNode.subNodes[3].subNodes[3].customisationVars, 'hud.polls.answerBoxStyle.hashtagAreaSpacing', 0, 'livePoll') * previewScale;
    const optionBoxBorderWidth = getCustomisationValue(livePollNode.subNodes[3].subNodes[3].customisationVars, 'hud.polls.answerBoxStyle.borderThickness', 0, 'livePoll') * previewScale;
    const optionBoxBorderColour = COLOUR_NAME_TO_COLOUR_HEX(getCustomisationValue(livePollNode.subNodes[3].subNodes[3].customisationVars, 'hud.polls.answerBoxStyle.borderColour', 0, 'livePoll'), campaignData);
    const hashtagGapColour = optionBoxBorderWidth > 0 ? optionBoxBorderColour : 'transparent';

    // console.log('LivePoll percBarWidth', percBarWidth, 'hashtagAreaGap', hashtagAreaGap);

    const [addingNewOption, setAddingNewOption] = useState(false);
    const [optionCountToRender, setOptionCountToRender] = useState(3);
    const optionsToRender_ref = useRef(-1);
    useEffect(() => {
        optionsToRender_ref.current = optionCountToRender;
    }, [optionCountToRender]);
    const renderMoreOptions = (e) => {
        setOptionCountToRender(prevCount => {
            if (lastPoll.index !== -1 && lastPoll.pollData.answers && lastPoll.pollData.answers.length >= 2) {
                prevCount = lastPoll.pollData.answers.length;
                setAddingNewOption(true);
                e.stopPropagation();
                return prevCount + 1;
            }
            if (prevCount < POLL_DUMMY_OPTIONS.length) {
                props.invalidateHighlightedNodeStates();
                return prevCount + 1;
            }
            return prevCount; // Don't exceed the number of options
        });
        // lastPoll.index = -1;
    }
    const renderLessOptions = () => {
        setOptionCountToRender(prevCount => {
            if (lastPoll.index !== -1 && lastPoll.pollData.answers && lastPoll.pollData.answers.length >= 2) {
                prevCount = lastPoll.pollData.answers.length;
            }
            if (prevCount > 2) {
                props.invalidateHighlightedNodeStates();
                return prevCount - 1;
            }
            return prevCount; // Don't go below 2
        });
        // lastPoll.index = -1;
    }

    const showPollHeaderText = getCustomisationValue(livePollNode.subNodes[0].customisationVars, 'hud.polls.panelStyle.useHeaderText', 0, 'livePoll', livePollNode);
    const headerCopy = getCustomisationValue(livePollNode.subNodes[0].customisationVars, 'hud.polls.panelStyle.headertText', 0, 'livePoll', livePollNode) || 'POLL';
    const showPollHeaderImage = getCustomisationValue(livePollNode.subNodes[0].customisationVars, 'hud.polls.panelStyle.useImage', 0, 'livePoll', livePollNode);
    const headerImageWidthPerc = getCustomisationValue(livePollNode.subNodes[0].customisationVars, 'hud.polls.panelStyle.imageWidth', 0, 'livePoll', livePollNode);
    const headerImageAlignment = getCustomisationValue(livePollNode.subNodes[0].customisationVars, 'hud.polls.panelStyle.headerImageAlignment', 0, 'livePoll', livePollNode);
    const headerImageSrc = getCustomisationValue(livePollNode.subNodes[0].customisationVars, 'hud.polls.panelStyle.filename', 0, 'livePoll', livePollNode).srcUrl || null;

    const secretPoll = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.shapeSettings.onlyShowPercAtEnd', 0, 'livePoll', livePollNode);
    const autoHighlightWinner = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.shapeSettings.fadeLosersAtEnd', 0, 'livePoll', livePollNode);
    const fadeWhileAwaitingOutcome = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.shapeSettings.fadeOptionWhileAwaitingOutcome', 0, 'livePoll', livePollNode);
    const autoFadeOptionsToRemove = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.shapeSettings.fadeOptionsToBeRemoved', 0, 'livePoll', livePollNode);

    // let headerTextBoldWeight = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.panelStyle.headerBoldWeight', 0, 'livePoll', livePollNode);
    // const headerTextIsBold = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.panelStyle.boldHeader', 0, 'livePoll', livePollNode);
    // if (!headerTextIsBold) headerTextBoldWeight = 0;
    const headerTextItalic = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.panelStyle.headerItalic', 0, 'livePoll', livePollNode);
    const capitaliseHeaderText = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.panelStyle.capitaliseHeader', 0, 'livePoll', livePollNode);
    const centerHeaderText = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.panelStyle.centerHeader', 0, 'livePoll', livePollNode);

    // let promptBoldWeight = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.panelStyle.promptBoldWeight', 0, 'livePoll', livePollNode);
    // const promptIsBold = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.panelStyle.boldPrompt', 0, 'livePoll', livePollNode);
    // if (!promptIsBold) promptBoldWeight = 0;
    const promptItalic = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.panelStyle.promptItalic', 0, 'livePoll', livePollNode);
    const capitalisePromptText = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.panelStyle.capitalisePrompt', 0, 'livePoll', livePollNode);

    // let syncMessageFontWeight = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.panelStyle.messageBoldWeight', 0, 'livePoll', livePollNode);
    // const syncMessageIsBold = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.panelStyle.messagesIsBold', 0, 'livePoll', livePollNode);
    // if (!syncMessageIsBold) syncMessageFontWeight = 0;
    const syncMessageItalic = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.panelStyle.messageItalic', 0, 'livePoll', livePollNode);
    const footerMessageStyle = {
        // fontWeight: 400 + (syncMessageFontWeight * 100),
        fontStyle: syncMessageItalic ? 'italic' : 'normal',
    }

    // let questionTextBoldWeight = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.questionTextSettings.boldWeight', 0, 'livePoll', livePollNode);
    // const questionTextIsBold = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.questionTextSettings.isBold', 0, 'livePoll', livePollNode);
    // if (!questionTextIsBold) questionTextBoldWeight = 0;
    const questionTextItalic = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.questionSettings.italic', 0, 'livePoll', livePollNode);
    const capitaliseQuestionText = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.questionSettings.capitalise', 0, 'livePoll', livePollNode);
    const questionTextStyle = {
        // fontWeight: 400 + (questionTextBoldWeight * 100),
        fontStyle: questionTextItalic ? 'italic' : 'normal',
        textTransform: capitaliseQuestionText ? 'capitalize' : 'none',
    }


    // let hashtagFontWeight = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.answerBoxStyle.hashtagBoldWeight', 0, 'livePoll', livePollNode);
    // const hashtagIsBold = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.answerBoxStyle.boldHashtag', 0, 'livePoll', livePollNode);
    // if (!hashtagIsBold) hashtagFontWeight = 0;
    const hashtagItalic = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.answerBoxStyle.hashtagItalic', 0, 'livePoll', livePollNode);
    const hashtagTextStyle = {
        // fontWeight: 400 + (hashtagFontWeight * 100),
        fontStyle: hashtagItalic ? 'italic' : 'normal',
    }

    // let answerTextFontWeight = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.answerBoxStyle.answerTextBoldWeight', 0, 'livePoll', livePollNode);
    // const answerTextIsBold = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.answerBoxStyle.boldAnswerText', 0, 'livePoll', livePollNode);
    // if (!answerTextIsBold) answerTextFontWeight = 0;
    const answerTextItalic = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.answerBoxStyle.answerTextItalic', 0, 'livePoll', livePollNode);
    const answerTextStyle = {
        // fontWeight: 400 + (answerTextFontWeight * 100),
        fontStyle: answerTextItalic ? 'italic' : 'normal',
    }

    // let pointsTextFontWeight = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.answerBoxStyle.pointsBoldWeight', 0, 'livePoll', livePollNode);
    // const pointsTextIsBold = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.answerBoxStyle.pointsIsBold', 0, 'livePoll', livePollNode);
    // if (!pointsTextIsBold) pointsTextFontWeight = 0;
    const pointsTextItalic = getCustomisationValue(livePollNode.customisationVars, 'hud.polls.answerBoxStyle.pointsItalic', 0, 'livePoll', livePollNode);
    const pointsTextStyle = {
        // fontWeight: 400 + (pointsTextFontWeight * 100),
        fontStyle: pointsTextItalic ? 'italic' : 'normal',
    }

    // let percentageTextFontWeight = getCustomisationValue(livePollNode.subNodes[3].subNodes[3].customisationVars, 'hud.polls.answerBoxStyle.percentageBoldWeight', 0, 'livePoll', livePollNode);
    // const percentageTextIsBold = getCustomisationValue(livePollNode.subNodes[3].subNodes[3].customisationVars, 'hud.polls.answerBoxStyle.boldPercentageText', 0, 'livePoll', livePollNode);
    // if (!percentageTextIsBold) percentageTextFontWeight = 0;
    const percentageTextItalic = getCustomisationValue(livePollNode.subNodes[3].subNodes[3].customisationVars, 'hud.polls.answerBoxStyle.percentageItalic', 0, 'livePoll', livePollNode);
    const percentageTextStyle = {
        // fontWeight: 400 + (percentageTextFontWeight * 100),
        fontStyle: percentageTextItalic ? 'italic' : 'normal',
    }

    // let correctMessageFontWeight = getCustomisationValue(livePollNode.subNodes[3].subNodes[5].customisationVars, 'hud.polls.answerBoxStyle.correctBoldWeight', 0, 'livePoll', livePollNode);
    // const correctMessageIsBold = getCustomisationValue(livePollNode.subNodes[3].subNodes[5].customisationVars, 'hud.polls.answerBoxStyle.correctIsBold', 0, 'livePoll', livePollNode);
    // if (!correctMessageIsBold) correctMessageFontWeight = 0;
    const correctMessageItalic = getCustomisationValue(livePollNode.subNodes[3].subNodes[5].customisationVars, 'hud.polls.answerBoxStyle.correctItalic', 0, 'livePoll', livePollNode);
    const correctMessageStyle = {
        // fontWeight: 400 + (correctMessageFontWeight * 100),
        fontStyle: correctMessageItalic ? 'italic' : 'normal',
    }


    let hidePointsText = getCustomisationValue(livePollNode.subNodes[4].customisationVars, 'hud.polls.answerBoxStyle.hidePointsText', 0, 'livePoll', livePollNode);
    // Some other times to hide points text
    if (props.timelinePlayheadLabel === 'Opinion Result' || props.timelinePlayheadLabel === 'Removal Result') {
        hidePointsText = true;
    }
    if (usingPollData && lastPoll.pollData.type !== 'predictive') {
        hidePointsText = true;
    }

    const [correctPredictionIndex, setCorrectPredictionIndex] = useState(-1);
    useEffect(() => {
        // Randomly set a correct prediction index when the poll is first rendered
        setCorrectPredictionIndex(Math.floor(Math.random() * Math.max(0, (usingPollData ? lastPoll.pollData.answers.length : optionCountToRender) - 1)));
    }, [optionCountToRender, props.timelinePlayheadStep, pollIndex]);
    const correctAnswerBgColor = COLOUR_NAME_TO_COLOUR_HEX(getCustomisationValue(livePollNode.subNodes[3].subNodes[3].customisationVars, 'hud.polls.answerBoxStyle.correctBackgroundColour', 0, 'livePoll', livePollNode), campaignData);
    const pointsUnit = getCustomisationValue(livePollNode.subNodes[3].subNodes[2].customisationVars, 'hud.polls.answerBoxStyle.pointsUnitCopy', 0, 'livePoll', livePollNode) || 'pts';

    const showPollPercs = !secretPoll || props.timelinePlayheadStep > 1;
    let opinionResult = -1;
    let perc = -1;
    for (let i = 0; i < POLL_DUMMY_OPTIONS.length; i++) {
        if (!usingPollData && i >= (optionsToRender_ref.current || optionCountToRender)) break;
        if (usingPollData && i >= lastPoll.pollData.answers.length) break;
        if (POLL_DUMMY_OPTIONS[i].perc > perc) {
            opinionResult = i;
            perc = POLL_DUMMY_OPTIONS[i].perc;
            break;
        }
    }
    let removalResult = -1;
    perc = 101;
    for (let i = 0; i < POLL_DUMMY_OPTIONS.length; i++) {
        if (!usingPollData && i >= (optionsToRender_ref.current || optionCountToRender)) break;
        if (usingPollData && i >= lastPoll.pollData.answers.length) break;
        if (POLL_DUMMY_OPTIONS[i].perc < perc) {
            removalResult = i;
            perc = POLL_DUMMY_OPTIONS[i].perc;
        }
    }

    const headerDividerColour = COLOUR_NAME_TO_COLOUR_HEX(getCustomisationValue(livePollNode.subNodes[0].customisationVars, 'hud.polls.panelStyle.headerDividerLineColour', 0, 'livePoll', livePollNode), campaignData);
    const headerDividerLineThickness = getCustomisationValue(livePollNode.subNodes[0].customisationVars, 'hud.polls.panelStyle.headerDividerLineThickness', 0, 'livePoll', livePollNode);
    const footerDividerColour = COLOUR_NAME_TO_COLOUR_HEX(getCustomisationValue(livePollNode.subNodes[4].customisationVars, 'hud.polls.panelStyle.timerBarDividerLineColour', 0, 'livePoll', livePollNode), campaignData);
    const footerDividerLineThickness = getCustomisationValue(livePollNode.subNodes[4].customisationVars, 'hud.polls.panelStyle.timerBarDividerLineThickness', 0, 'livePoll', livePollNode);
    

    // Top level renderers need to be wrapped in a VisualisedNode, so we can pass the props and style
    // Top level means the key in CustomComponentsList is the id of the feature / disruptor
    return (
        <VisualisedNode
            {...props}
            topLevel={true}
            style={{height: 'fit-content', margin: 45 * previewScale}}
            node={livePollNode}
        >
            {/* The first sub node is the poll header */}
            {(showPollHeaderText || showPollHeaderImage) &&
                <VisualisedNode
                    {...props}
                    node={livePollNode.subNodes[0]}
                    style={{borderBottom: `${headerDividerLineThickness * previewScale}px solid ${headerDividerColour}`}}
                >
                    {showPollHeaderImage && headerImageSrc &&
                        <>
                            <VisualisedNode 
                                {...props}
                                node={livePollNode.subNodes[0].subNodes[0]}
                                style={{
                                    width: headerImageWidthPerc ? `${headerImageWidthPerc}%` : 'auto',
                                    alignSelf: headerImageAlignment === 'left' ? 'flex-start' : (headerImageAlignment === 'right' ? 'flex-end' : 'center'),
                                }}
                                // style={{padding: `${30 * previewScale}px ${30 * previewScale}px`}}
                                // contentNode={true}
                                // fixedContent={'hud.polls.panelStyle.headertText'}
                            />
                            {/* <img
                                src={headerImageSrc}
                                alt="Poll Header"
                                style={{
                                    width: headerImageWidthPerc ? `${headerImageWidthPerc}%` : 'auto',
                                    alignSelf: headerImageAlignment === 'left' ? 'flex-start' : (headerImageAlignment === 'right' ? 'flex-end' : 'center'),
                                }}
                            /> */}
                        </>
                    }
                    {/* Using a VisualisedNode for the copy and setting fixedContent means our VisualisedNode component will handle editing text in place */}
                    {showPollHeaderText &&
                        <VisualisedNode 
                            {...props}
                            node={livePollNode.subNodes[0].subNodes[1]}
                            style={{
                                padding: `${30 * previewScale}px ${30 * previewScale}px`,
                                // fontWeight: 400 + (headerTextBoldWeight * 100),
                                fontStyle: headerTextItalic ? 'italic' : 'normal',
                                textTransform: capitaliseHeaderText ? 'capitalize' : 'none',
                                textAlign: centerHeaderText ? 'center' : 'left',
                                alignSelf: centerHeaderText ? 'center' : 'flex-start',
                            }}
                            // contentNode={true}
                            // fixedContent={'hud.polls.panelStyle.headertText'}
                        />
                    }
                </VisualisedNode>
            }
            {/* Question text is next, but at this point we want a flex column with 30px gap */}
            <div className="v2-live-poll-content" style={{gap: `${30 * previewScale}px`}}>
                <VisualisedNode
                    {...props}
                    node={livePollNode.subNodes[1]}
                    style={{
                        ...questionTextStyle,
                        padding: `0px ${30 * previewScale}px 0px`, marginTop: `${30 * previewScale}px`
                    }}
                >
                    {!usingPollData && <>Question Text</>}
                    {usingPollData && 
                        <VisualisedNode
                            {...props}
                            node={livePollNode.parent.subNodes[1]}
                            textEditMode={true}
                            textEditModeFieldPath={'setup.pollData.polls.question'}
                            textEditModeContentNode={livePollNode.parent.subNodes[1].subNodes[lastPoll.index]}
                            multiline={true}
                            index={lastPoll.index}
                        >
                            {/*lastPoll.pollData.question*/}
                        </VisualisedNode>
                    }
                </VisualisedNode>
                {/* Prompt text next, we want to control the padding*/}
                <VisualisedNode
                    {...props}
                    node={livePollNode.subNodes[2]}
                    style={{
                        padding: `0 ${30 * previewScale}px`,
                        // fontWeight: 400 + (promptBoldWeight * 100),
                        fontStyle: promptItalic ? 'italic' : 'normal',
                        textTransform: capitalisePromptText ? 'capitalize' : 'none',
                    }}
                />
                {/* Now we are going to add in 4 example options */}
                <div className="v2-live-poll-content__options-container" style={{padding: `0 ${30 * previewScale}px`, gap: `${10 * previewScale}px`}}>
                    {POLL_DUMMY_OPTIONS.map((option, index) => {
                        if ((!usingPollData && index >= optionCountToRender) || (usingPollData && index >= lastPoll.pollData.answers.length)) {
                            return null; // Don't render more than the set count
                        } else 
                        return (
                            <VisualisedNode
                                key={index}
                                {...props}
                                node={livePollNode.subNodes[3]}
                                style={
                                    {
                                        width: '100%',
                                        opacity: (fadeWhileAwaitingOutcome && props.timelinePlayheadStep === 3) ? 0.5 : 
                                                 (autoHighlightWinner && props.timelinePlayheadStep === 2 && index !== opinionResult) ? 0.5 : 
                                                 (autoFadeOptionsToRemove && props.timelinePlayheadStep === 5 && index === removalResult) ? 0.5 : 1,
                                    }
                                }
                                // clickable={index === 0}
                            >
                                <>
                                    {/* The option box consists of 4 sub nodes: hashtag area, answer text, percentage text and percentage bar (in that order) */}
                                    <VisualisedNode
                                        {...props}
                                        node={livePollNode.subNodes[3].subNodes[0]}
                                        style={{...hashtagTextStyle, padding: `0 ${20 * previewScale}px`, justifyContent: 'left', alignItems: 'center', minWidth: 'fit-content'}}
                                        clickable={index === 0}
                                    >
                                        {option.hashtag}
                                    </VisualisedNode>
                                    <div
                                        style={{
                                            width: hashtagAreaGap,
                                            height: '100%',
                                            backgroundColor: hashtagGapColour,
                                        }}
                                    />
                                    <div className="v2-live-poll-content__option-answer-area" 
                                        style={
                                            {
                                                width: percBarWidth, 
                                                // marginLeft: hashtagAreaGap,
                                                backgroundColor: (props.timelinePlayheadStep !== 4 || index !== correctPredictionIndex) ? 'transparent' : correctAnswerBgColor,
                                            }
                                        }
                                    >
                                        <div className="v2-live-poll-content__option-perc-bar" style={{width: percBarWidth}}>
                                            <VisualisedNode
                                                {...props}
                                                node={livePollNode.subNodes[3].subNodes[4]}
                                                style={{width: '100%', height: '100%'}}
                                                clickable={index === 0}
                                            >
                                                {/* The percentage bar is a sub node of the option node, we can just render it */}
                                            {(props.timelinePlayheadStep !== 4 || index !== correctPredictionIndex) &&
                                                <InlineProgressBar
                                                    viewId={`live-poll-perc-${livePollNode.id}-${index}`}
                                                    customSettings={percBarSettings}
                                                    fillPerc={showPollPercs ? option.perc / 100 : 0}
                                                    useOutline={false}
                                                    width={percBarWidth}
                                                    height={getCustomisationValue(livePollNode.subNodes[3].subNodes[4].customisationVars, 'hud.polls.answerBoxStyle.height') * props.previewScale}
                                                    stripeWidth={10}
                                                    previewScale={props.previewScale}
                                                />
                                            }
                                            </VisualisedNode>
                                        </div>
                                        <div className="v2-live-poll-content__option-perc-bar" style={{width: percBarWidth}}>

                                            {props.timelinePlayheadStep === 4 && index === correctPredictionIndex &&
                                                <VisualisedNode
                                                    {...props}
                                                    node={livePollNode.subNodes[3].subNodes[5]}
                                                    style={{...correctMessageStyle, width: '100%', height: '100%'}}
                                                >
                                                </VisualisedNode>
                                            }
                                        </div>

                                        <div className="v2-live-poll-content__option-over-bar-content" style={{padding: `0 ${20 * previewScale}px`}}>
                                            <div className={`v2-live-poll-content__option-answer-points-column${props.editingTextContentData ? ' grow' : ''}`}>
                                                <VisualisedNode
                                                    {...props}
                                                    style = {{...answerTextStyle, width: props.editingTextContentData ? '100%' : 'fit-content'}}
                                                    node={livePollNode.subNodes[3].subNodes[1]}
                                                    // clickable={index === 0}
                                                >
                                                    {!usingPollData && <>{option.text}</>}
                                                    {usingPollData && 
                                                        <VisualisedNode
                                                            {...props}
                                                            node={livePollNode.parent.subNodes[1]}
                                                            textEditMode={true}
                                                            textEditModeFieldPath={'setup.pollData.polls.answer' + (index + 1)}
                                                            textEditModeContentNode={livePollNode.parent.subNodes[1].subNodes[lastPoll.index]}
                                                            multiline={false}
                                                            index={lastPoll.index}
                                                            style={{ ...answerTextStyle, width: '100%'}}
                                                        >
                                                            {/*lastPoll.pollData.answers[index]*/}
                                                        </VisualisedNode>
                                                    }
                                                </VisualisedNode>
                                                {!hidePointsText &&
                                                    <VisualisedNode
                                                        {...props}
                                                        style = {{...pointsTextStyle, width: 'fit-content'}}
                                                        node={livePollNode.subNodes[3].subNodes[2]}
                                                        clickable={index === 0}
                                                    >
                                                        {usingPollData ? lastPoll.pollData.points[index] : option.points}{pointsUnit}
                                                    </VisualisedNode>
                                                }
                                            </div>
                                            {!props.editingTextContentData &&
                                                <div className="grow"
                                                    style={{height: '100%'}}
                                                    onClick={
                                                        (!props.editingTextContentData && (index === 0 || (props.timelinePlayheadStep === 4 && index === correctPredictionIndex))) ?
                                                            (e) => {
                                                                e.stopPropagation();
                                                                e.preventDefault();
                                                                if (!props.draggingActive.current) {
                                                                    props.draggingCancelled.current = true;
                                                                    // Use this to select the percentage bar node, as we can't click straight through to it
                                                                    props.activateNodeFunction(livePollNode.subNodes[3].subNodes[props.timelinePlayheadStep === 4 && index === correctPredictionIndex ? 5 : 4]);
                                                                }
                                                            }
                                                            : null
                                                    }
                                                    onMouseOverCapture={(index === 0 || (props.timelinePlayheadStep === 4 && index === correctPredictionIndex)) ? (e) => props.highlightHoveredNode(e, livePollNode.subNodes[3].subNodes[props.timelinePlayheadStep === 4 && index === correctPredictionIndex ? 5 : 4]) : null}
                                                    onMouseLeave={(index === 0 || (props.timelinePlayheadStep === 4 && index === correctPredictionIndex)) ? (e) => props.unhighlightHoveredNode(e, livePollNode.subNodes[3].subNodes[props.timelinePlayheadStep === 4 && index === correctPredictionIndex ? 5 : 4]) : null}
                                                ></div>
                                            }
                                            {showPollPercs && ((!usingPollData && index < optionCountToRender - 1) || ((usingPollData && index < lastPoll.pollData.answers.length - 1) || addingNewOption)) && (props.timelinePlayheadStep !== 4 || index !== correctPredictionIndex) &&
                                                <VisualisedNode
                                                    {...props}
                                                    style = {{...percentageTextStyle, width: 'fit-content'}}
                                                    node={livePollNode.subNodes[3].subNodes[3]}
                                                    clickable={index === 0}
                                                >
                                                    {option.perc}%
                                                </VisualisedNode>
                                            }
                                            {props.timelinePlayheadStep === 4 && index === correctPredictionIndex &&
                                                <VisualisedNode
                                                    {...props}
                                                    style = {{...correctMessageStyle, width: 'fit-content'}}
                                                    node={livePollNode.subNodes[3].subNodes[5]}
                                                >
                                                    {!usingPollData &&<>Correct!</>}
                                                    {usingPollData &&<>{lastPoll.rawPollData.correctCopy}</>}
                                                </VisualisedNode>
                                            }
                                            {!props.thumbnailMode && ((!usingPollData && index === optionCountToRender - 1) || (usingPollData && index === lastPoll.pollData.answers.length - 1 && !addingNewOption)) &&
                                                <>
                                                    {!usingPollData &&
                                                        <div className="v2-live-poll-options-up-down-button" onClick={renderLessOptions}>-</div>
                                                    }
                                                    {(!usingPollData || !addingNewOption) && index < POLL_DUMMY_OPTIONS.length - 1 &&
                                                        <div className="v2-live-poll-options-up-down-button" onClick={renderMoreOptions}>+</div>
                                                    }
                                                </>
                                            }
                                        </div>
                                    </div>
                                </>
                            </VisualisedNode>
                        )
                    })}
                    {addingNewOption && usingPollData &&
                        <VisualisedNode
                            {...props}
                            node={livePollNode.subNodes[3]}
                            style={
                                {
                                    width: '100%',
                                    opacity: 1,
                                }
                            }
                            clickable={true}
                        >
                            <>
                                <VisualisedNode
                                    {...props}
                                    node={livePollNode.subNodes[3].subNodes[0]}
                                    style={{...hashtagTextStyle, padding: `0 ${20 * previewScale}px`, justifyContent: 'left', alignItems: 'center', minWidth: 'fit-content'}}
                                    clickable={true}
                                >
                                    {POLL_DUMMY_OPTIONS[lastPoll.pollData.answers.length].hashtag ?? '#J'}
                                </VisualisedNode>

                                <div className="v2-live-poll-content__option-answer-area" 
                                        style={
                                            {
                                                width: percBarWidth, 
                                                marginLeft: hashtagAreaGap,
                                                backgroundColor: 'transparent',
                                            }
                                        }
                                    >
                                        <div className="v2-live-poll-content__option-perc-bar" style={{width: percBarWidth}}>
                                            <VisualisedNode
                                                {...props}
                                                node={livePollNode.subNodes[3].subNodes[4]}
                                                style={{width: '100%', height: '100%'}}
                                                clickable={false}
                                            >
                                                {/* <InlineProgressBar
                                                    viewId={`live-poll-perc-${livePollNode.id}-${index}`}
                                                    customSettings={percBarSettings}
                                                    fillPerc={showPollPercs ? option.perc / 100 : 0}
                                                    useOutline={false}
                                                    width={percBarWidth}
                                                    height={getCustomisationValue(livePollNode.subNodes[3].subNodes[4].customisationVars, 'hud.polls.answerBoxStyle.height') * props.previewScale}
                                                /> */}
                                            </VisualisedNode>
                                        </div>

                                        <div className="v2-live-poll-content__option-over-bar-content" style={{padding: `0 ${20 * previewScale}px`, width: '100%'}}>
                                            <div className="v2-live-poll-content__option-answer-points-column" style={{width: '100%'}}>
                                                <VisualisedNode
                                                    {...props}
                                                    style = {{width: '100%'}}
                                                    node={livePollNode.subNodes[3].subNodes[1]}
                                                    clickable={true}
                                                >
                                                    <VisualisedNode
                                                        {...props}
                                                        node={livePollNode.parent.subNodes[1]}
                                                        textEditMode={true}
                                                        textEditModeFieldPath={'setup.pollData.polls.answer' + (lastPoll.pollData.answers.length + 1)}
                                                        textEditModeContentNode={livePollNode.parent.subNodes[1].subNodes[lastPoll.index]}
                                                        forceEdit={true}
                                                        placeholder={'Enter option text...'}
                                                        valueChangedCallback={() => {setAddingNewOption(false); props.forceUpdateModuleListFunc();}}
                                                        multiline={false}
                                                        index={lastPoll.index}
                                                        style={{...answerTextStyle, width: `calc(100% - ${20 * previewScale}px - 1.8rem)`, marginRight: '1rem'}}
                                                    >
                                                        {/*lastPoll.pollData.answers[index]*/}
                                                    </VisualisedNode>
                                                </VisualisedNode>
                                            </div>
                                            {!props.thumbnailMode &&
                                                <>
                                                    <div className="v2-red-circle-delete-icon-button" style={{position: 'absolute', right: `${20 * previewScale}px`, top: '50%', transform: 'translateY(-50%)', zIndex: 10000}} onClick={(e) => {e.stopPropagation(); setAddingNewOption(false);}}>
                                                        {IconJsxer.GetIcon(IconJsxer.ICONS.trash, IconJsxer.ICON_STYLES.roundPanelButtonSmall, 'delete-icon')}
                                                    </div>
                                                    {/* <div className="v2-live-poll-options-up-down-button v2-small-x-close-button" onClick={(e) => {e.stopPropagation(); setAddingNewOption(false);}}>X</div> */}
                                                </>
                                            }
                                        </div>
                                    </div>

                            </>
                        </VisualisedNode>
                    }
                </div>
                {/* Finally, we have the poll footer */}
                <VisualisedNode
                    {...props}
                    node={livePollNode.subNodes[4]}
                    style={
                        {
                            padding: `${30 * previewScale}px`,
                            position: 'relative',
                            whiteSpace: 'nowrap', 
                            overflow: 'hidden',
                            borderTop: `${footerDividerLineThickness * previewScale}px solid ${footerDividerColour}`,
                        }
                    }
                    contentStyle={{
                        height: `${getCustomisationValue(livePollNode.subNodes[4].customisationVars, 'hud.polls.timerBarStyle.timerBarThickness') * previewScale}px`,
                        overflow: 'visible',
                        position: 'relative',
                    }}
                    innerContentStyle={{
                        position: 'absolute',
                        top: '50%',
                        transform: 'translateY(-50%)',
                    }}
                >
                    {/* Check if this is a version of the footer than includes a progress bar */}
                    {/*livePollNode.subNodes[4].subNodes.length > 0 && livePollNode.subNodes[4].subNodes[0]?.name?.toLowerCase()?.includes(' bar') &&*/
                        <>
                            <VisualisedNode
                                {...props}
                                node={livePollNode.subNodes[4].subNodes[0]}
                                style={{width: '100%'}}
                            >
                                {/* The footer has a sub node for the poll timer, we can just render that */}
                                <InlineProgressBar
                                    viewId={`live-poll-timer-${livePollNode.id}`}
                                    customSettings={convertCustomisationDataToBarSettings(getGroupData(livePollNode.subNodes[4].subNodes[0].customisationVars, 'hud.polls.timerBarStyle'))}
                                    fillPerc={0.7}
                                    useOutline={false}
                                    width={
                                        ((getCustomisationValue(livePollNode.subNodes[4].subNodes[0].customisationVars, 'hud.polls.shapeSettings.panelWidth'))
                                        - (getCustomisationValue(livePollNode.subNodes[4].subNodes[0].customisationVars, 'hud.polls.shapeSettings.borderThickness') * 2)
                                        - 60) * props.previewScale
                                    }
                                    height={getCustomisationValue(livePollNode.subNodes[4].subNodes[0].customisationVars, 'hud.polls.timerBarStyle.timerBarThickness') * props.previewScale}
                                    stripeWidth={10}
                                    previewScale={props.previewScale}
                                />
                            </VisualisedNode>
                            <VisualisedNode
                                {...props}
                                node={livePollNode.subNodes[4].subNodes[1]}
                                style={{...footerMessageStyle, position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)'}}
                            >
                            </VisualisedNode>
                            <VisualisedNode
                                {...props}
                                node={livePollNode.subNodes[4].subNodes[2]}
                                style={{...footerMessageStyle, position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)'}}
                            >
                                {!usingPollData && <>- VOTING ENDED -</>}
                                {usingPollData && <>{lastPoll.rawPollData.voteEndMessageOpinion}</>}
                            </VisualisedNode>
                            <VisualisedNode
                                {...props}
                                node={livePollNode.subNodes[4].subNodes[3]}
                                style={{...footerMessageStyle, position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)'}}
                            >
                                {!usingPollData && <>- AWAITING OUTCOME -</>}
                                {usingPollData && <>{lastPoll.rawPollData.awaitingOutcomeCopy}</>}
                            </VisualisedNode>
                            <VisualisedNode
                                {...props}
                                node={livePollNode.subNodes[4].subNodes[4]}
                                style={{...footerMessageStyle, position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)'}}
                            >
                                {!usingPollData && <>99999 PLAYERS GOT IT RIGHT!</>}
                                {usingPollData && <>99999&nbsp;{lastPoll.rawPollData.playersCorrectCopy}</>}
                            </VisualisedNode>
                            <VisualisedNode
                                {...props}
                                node={livePollNode.subNodes[4].subNodes[5]}
                                style={{...footerMessageStyle, position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)'}}
                            >
                                {!usingPollData && <>- VOTING ENDED -</>}
                                {usingPollData && <>{lastPoll.rawPollData.voteEndMessageRemoval}</>}
                            </VisualisedNode>
                        </>
                    }
                </VisualisedNode>
            </div>
        </VisualisedNode>
    )
}

export default LivePoll;
